import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

/**
 * Which opening the web2app funnels show, decided by a PostHog multivariate
 * feature flag so the three can be A/B tested from the PostHog UI without a
 * deploy.
 *
 *   control        — the welcome screen as it is today
 *   question-first — a screen-time question in place of the welcome screen
 *                    (/funnel-auth), or after the explainer slides
 *                    (/funnel-auth/intro)
 *   no-welcome     — no opening screen: straight to picking a friend, or
 *                    straight into the explainer slides on the intro funnel
 *
 * The flag in PostHog: key `funnel-welcome-screen`, variants named exactly as
 * above. Anything else the flag returns (off, unknown variant, PostHog
 * unreachable) is treated as control, so the funnels can never break on the
 * flag's account.
 *
 * Once decided, the variant is pinned in sessionStorage for the rest of the
 * funnel session: the Google round trip reloads the page, sign-in switches
 * PostHog's distinct_id (which can re-bucket the visitor), and a visitor must
 * not meet a second opening halfway through. It's also what every funnel event
 * reports as `welcome_variant`, so the drop-off can be split by what each
 * visitor actually saw rather than by what the flag says now.
 *
 * For QA, `?welcome_variant=<variant>` on the funnel URL forces one.
 */

export const WELCOME_FLAG_KEY = 'funnel-welcome-screen';
export const WELCOME_VARIANTS = ['control', 'question-first', 'no-welcome'];
export const WELCOME_VARIANT_KEY = 'rc_funnel_welcome_variant';
const OVERRIDE_PARAM = 'welcome_variant';

// How long a first-time visitor waits for PostHog's flag response before
// getting control. This is a ceiling, not the usual wait: posthog-js asks for
// flags at app boot, before React renders the funnel, so by the time this
// page mounts the answer is normally already in. Returning visitors never
// wait — the flag posthog-js persisted on their last visit decides at once.
export const FLAG_TIMEOUT_MS = 1000;

const asVariant = (value) => (WELCOME_VARIANTS.includes(value) ? value : null);

const readPinned = () => {
  try {
    return asVariant(sessionStorage.getItem(WELCOME_VARIANT_KEY));
  } catch (_) {
    return null;
  }
};

const pin = (variant) => {
  try {
    sessionStorage.setItem(WELCOME_VARIANT_KEY, variant);
  } catch (_) {
    // Private mode etc. — the variant just isn't pinned across a reload.
  }
};

const readOverride = () => {
  try {
    return asVariant(new URLSearchParams(window.location.search).get(OVERRIDE_PARAM));
  } catch (_) {
    return null;
  }
};

// Reads the flag (and, as a side effect, has posthog-js emit the
// `$feature_flag_called` exposure event the experiment is measured on).
// Before this page load's flags are back, posthog-js answers from the flags it
// persisted last time — so a returning visitor is decided without waiting —
// and with nothing persisted it answers undefined.
const readFlag = () => {
  try {
    return asVariant(posthog.getFeatureFlag(WELCOME_FLAG_KEY));
  } catch (_) {
    return null;
  }
};

const flagsReady = () => {
  try {
    return !!posthog.featureFlags?.hasLoadedFlags;
  } catch (_) {
    return false;
  }
};

/**
 * @returns {string|null} The variant, or null while it's still being decided —
 *   the page should hold its opening screen back until then (never for a
 *   session that already has one pinned).
 */
export const useWelcomeVariant = () => {
  const [variant, setVariant] = useState(() => {
    // An explicit ?welcome_variant beats the pinned one, so QA can walk the
    // three arms in one tab without clearing sessionStorage between them
    const forced = readOverride();
    if (forced) {
      pin(forced);
      return forced;
    }
    const pinned = readPinned();
    if (pinned) return pinned;
    // PostHog disabled (no key, localhost) → nothing to wait for
    if (!posthog.__loaded) {
      pin('control');
      return 'control';
    }
    // Flags already in, or persisted from an earlier visit → decide on the
    // spot, no blank frame
    const now = readFlag();
    if (now) {
      pin(now);
      return now;
    }
    if (flagsReady()) {
      pin('control');
      return 'control';
    }
    return null;
  });

  useEffect(() => {
    if (variant) return undefined;

    let settled = false;
    const settle = (next) => {
      if (settled) return;
      settled = true;
      pin(next);
      setVariant(next);
    };

    let unsubscribe = () => {};
    try {
      unsubscribe = posthog.onFeatureFlags(() => settle(readFlag() || 'control')) || unsubscribe;
    } catch (_) {
      settle('control');
    }
    const timer = setTimeout(() => settle('control'), FLAG_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
      try {
        unsubscribe();
      } catch (_) {
        // ignore
      }
    };
  }, [variant]);

  return variant;
};

export default useWelcomeVariant;
